import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { ArrowRight } from 'lucide-react';
import { useLocale } from '../contexts/LocaleContext';
import { getAppLocale } from '../content/appLocale';
import LocaleSelector from '../components/LocaleSelector';
import StudioBrand from '../components/StudioBrand';
import '../styles/studio.css';

const Login = () => {
  const { locale } = useLocale();
  const copy = getAppLocale(locale);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');

  const [error, setError] = useState('');
  const [loadingEmail, setLoadingEmail] = useState(false);
  const [loadingGoogle, setLoadingGoogle] = useState(false);

  const {
    login,
    loginWithGoogle,
    isFirebaseClientConfigured,
  } = useAuth();
  const navigate = useNavigate();

  const handleEmailSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoadingEmail(true);
    const result = await login(email, password);
    if (result.success) {
      navigate('/dashboard');
    } else {
      setError(result.error);
    }
    setLoadingEmail(false);
  };

  const handleGoogle = async () => {
    setError('');
    setLoadingGoogle(true);
    const result = await loginWithGoogle();
    if (result.success) {
      navigate('/dashboard');
    } else {
      setError(result.error);
    }
    setLoadingGoogle(false);
  };

  const busy = loadingEmail || loadingGoogle;
  return (
    <div className="adfini-studio studio-login">
      <a className="studio-skip" href="#login-main">Skip to sign in</a>
      <header className="studio-login-header"><StudioBrand to="/" /><LocaleSelector className="studio-login-locale" /></header>
      <main id="login-main" className="studio-login-layout">
        <section className="studio-login-form-region" aria-labelledby="login-title">
          <div className="studio-login-form-content">
            <h1 id="login-title">{copy.auth.welcomeBack}</h1>
            <p className="studio-login-description">{copy.auth.signInSubtitle}</p>
            {error && <div role="alert" data-testid="login-error-message" className="studio-alert studio-alert-error">{error}</div>}
            {!isFirebaseClientConfigured && <p role="status" className="studio-alert">{copy.auth.firebaseSigninHelp}</p>}
            {isFirebaseClientConfigured && <>
              <button type="button" data-testid="login-google-button" disabled={busy} onClick={handleGoogle} className="studio-button studio-button-secondary studio-login-google">
                {loadingGoogle ? copy.auth.openGoogle : <><svg width="20" height="20" viewBox="0 0 24 24" aria-hidden>
                  <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
                  <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
                  <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
                  <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
                </svg>{copy.auth.continueGoogle}</>}
              </button>
              <p className="studio-login-divider">{copy.auth.orEmail}</p>
              <form onSubmit={handleEmailSubmit} className="studio-login-form">
                <fieldset disabled={busy}>
                  <div><label htmlFor="login-email" className="studio-field-label">{copy.auth.emailAddress}</label><input id="login-email" name="email" data-testid="login-email-input" type="email" autoComplete="username" value={email} onChange={e => setEmail(e.target.value)} className="studio-input" placeholder="you@example.com" required /></div>
                  <div><div className="studio-login-password-label"><label htmlFor="login-password" className="studio-field-label">{copy.auth.password}</label><Link to="/forgot-password">{copy.auth.forgotPassword}</Link></div><input id="login-password" name="password" data-testid="login-password-input" type="password" autoComplete="current-password" value={password} onChange={e => setPassword(e.target.value)} className="studio-input" placeholder="Enter your password" required /></div>
                  <button data-testid="login-submit-button" type="submit" className="studio-button studio-button-primary" disabled={busy}>{loadingEmail ? copy.auth.signingIn : copy.auth.signIn}<ArrowRight size={17} aria-hidden /></button>
                </fieldset>
              </form>
            </>}
            <p className="studio-login-register">{copy.auth.noAccount} <Link to="/register" data-testid="register-link">{copy.auth.signUp}<ArrowRight size={14} aria-hidden /></Link></p>
            <nav className="studio-login-help" aria-label="Sign-in help"><Link to="/help">Help & support</Link><Link to="/privacy">Privacy policy</Link></nav>
          </div>
        </section>
        <aside className="studio-login-story" aria-label="Your next production">
          <figure><img src="/media/studio/skincare.jpg" alt="Amber product bottle on a wooden stand in natural light" width="600" height="650" /><figcaption>Reference photography / <a href="https://unsplash.com" target="_blank" rel="noreferrer">Unsplash</a></figcaption></figure>
          <h2>Your product.<br />Your next production.</h2>
          <p>Bring your photos and a brief. Pick up the story in your workspace.</p>
        </aside>
      </main>
    </div>
  );
};

export default Login;
